import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import RateReleaseIntervalForm from './RateReleaseIntervalForm';
import { ReleaseIntervalRuleDetails } from '../../../common/types/rates.types';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';
import {
  getDefaultRateReleaseIntervalRuleAsync,
  selectReleaseIntervalRuleDetails,
  selectReleaseIntervalRuleDetailsLoadingStatus,
  updateDefaultRateReleaseIntervalRuleAsync,
} from '../defaultRateRulesSlice';
import {
  copyRateRuleReleaseIntervalToSeasons,
  deleteRulesFromSeasonsAsync,
} from '../../defaultRateTools/defaultRateToolsSlice';
import { SEASON_RATE_RULE_TYPE } from '../../../common/constants';

function DefaultRateReleaseInterval({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const releaseIntervalRuleDetails = useAppSelector(
    selectReleaseIntervalRuleDetails,
  );
  const releaseIntervalItemStatus = useAppSelector(
    selectReleaseIntervalRuleDetailsLoadingStatus,
  );
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  const initialData: ReleaseIntervalRuleDetails = {
    defaultRateId,
    daysUntilRelease: null,
    releaseLengthOfStayRule: true,
    releaseWeekdayMinimumStayRules: true,
    releaseWeekdayCheckOutRules: true,
    releaseWeekdayCheckInRules: true,
    isActive: true,
    isCopyable: true,
  };

  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getDefaultRateReleaseIntervalRuleAsync(defaultRateId));
  }, [dispatch, defaultRateId]);

  useEffect(() => {
    if (
      releaseIntervalRuleDetails &&
      releaseIntervalRuleDetails.defaultRateId.toLowerCase() ===
        defaultRateId.toLowerCase()
    ) {
      setData(releaseIntervalRuleDetails);
    } else {
      setData(initialData);
    }
  }, [releaseIntervalRuleDetails]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setData(newData);
  };

  const onSubmit = () => {
    dispatch(updateDefaultRateReleaseIntervalRuleAsync(data));
    setSubmitted(true);
  };

  const onDelete = () => {
    if (
      window.confirm('Haluatko varmasti poistaa säännöt valituilta kausilta?')
    ) {
      dispatch(
        deleteRulesFromSeasonsAsync({
          accommodationId,
          selectedSeasonRateIds,
          ruleType: SEASON_RATE_RULE_TYPE.RateRuleReleaseInterval,
        }),
      );
      setSubmitted(false);
      setData(initialData);
      setDeleted(true);
    }
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyRateRuleReleaseIntervalToSeasons({
          accommodationId,
          selectedSeasonRateIds,
          daysUntilRelease: data.daysUntilRelease,
          releaseLengthOfStayRule: data.releaseLengthOfStayRule,
          releaseWeekdayMinimumStayRules: data.releaseWeekdayMinimumStayRules,
          releaseWeekdayCheckOutRules: data.releaseWeekdayCheckOutRules,
          releaseWeekdayCheckInRules: data.releaseWeekdayCheckInRules,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <RateReleaseIntervalForm
        loadingStatus={releaseIntervalItemStatus}
        handleInputChange={handleInputChange}
        onSubmit={onSubmit}
        onDelete={onDelete}
        submitted={submitted}
        deleted={deleted}
        data={data}
        hasWriteAccess={hasWriteAccess}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateReleaseInterval;
